import React, { useState, useEffect, useCallback } from "react";
import DataTableComponent from "../../../common/DataTable/PrimeDataTable";
import { apiService } from "./data";
import { getTableColumns } from "./tableConfig";
import { AddCommentModal, ViewCommentsModal, CommentPopupMenu } from "./components";
import { useCommentManagement, shouldClosePopup } from "./hooks";
import { useFormActions } from "../../../../hooks/useFormActions";
import { useComments } from "../../../../hooks/useComments";
import "./styles.css";
import { useParams } from "react-router-dom";
import { STATUSES } from "../../../../constants/statuses";

export default function CoreValidatorList({requestProgressId, requestProgress, children, requestId}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Always call useParams at the top level
  const params = useParams();
  const finalRequestProgressId = requestProgressId || params.request_progress_id;

  // Add hooks for comments and form actions
  const { prevComments } = useComments({
    persona: "admin",
    requestId: requestId || requestProgress?.request_id,
    skipInitialFetch: false,
  });

  // Load data function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const records = await apiService.getValidationRecords(finalRequestProgressId, requestProgress?.status);
      setData(records);
    } catch (error) {
      console.error("Error loading data:", error);
      // Fallback to empty data in case of error
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [finalRequestProgressId]);

  // Custom hooks for managing state and actions
  const commentManagement = useCommentManagement(finalRequestProgressId, loadData);

  // Load data on component mount
  useEffect(() => {
    if (finalRequestProgressId) {
      loadData();
    }
  }, [finalRequestProgressId, loadData]);

  // Table columns configuration
  const columns = getTableColumns(commentManagement.handleCommentIconClick);

  const isRequestReviewed = data.find(item => item.status && item.status !== "-");
  if(!isRequestReviewed) {
    columns.splice(columns.length - 1, 1);
  }

  // Handle container click to close popup
  const handleContainerClick = (e) => {
    if (shouldClosePopup(e)) {
      commentManagement.closePopup();
    }
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="container" onClick={handleContainerClick}>
      <div className="content-card">
        <DataTableComponent
          data={data}
          columns={columns}
          enablePagination={false}
          totalRecords={data.length}
        />
      </div>

      {/* Comment Popup Menu */}
      <CommentPopupMenu
        isOpen={commentManagement.popupMenuOpen}
        position={commentManagement.popupPosition}
        onViewComments={commentManagement.handleViewComments}
        onAddComment={commentManagement.handleAddCommentClick}
        onClose={commentManagement.closePopup}
        requestProgress={requestProgress}
      />

      {/* Add Comment Modal */}
      <AddCommentModal
        isOpen={commentManagement.isCommentModalOpen}
        commentText={commentManagement.commentText}
        onCommentChange={commentManagement.setCommentText}
        selectedFile={commentManagement.selectedFile}
        onFileChange={commentManagement.setSelectedFile}
        onSubmit={commentManagement.handleCommentSubmit}
        onClose={commentManagement.closeCommentModal}
      />

      {/* View Comments Modal */}
      <ViewCommentsModal
        isOpen={commentManagement.isViewCommentsModalOpen}
        selectedRow={commentManagement.selectedRow}
        onClose={commentManagement.closeViewCommentsModal}
      />
    </div>
  );
}
